import { assign, createMachine, DoneEventObject } from 'xstate'
import { Order, Tier, TierHierarchy } from '@mc/api'
import { getBogoSendGiftMachineConfig } from '@mc/bogo'
import type { FreeTrial } from '@mc/free-trial'
import { hasSelectedAudioReferral } from '@mc/growth-data'
import { paths } from '@mc/paths'
import type { PurchaseSource } from '@mc/payments'
import { CheckoutEvents } from '@mc/pre-paywall-events'
import { useStateMachine } from '@mc/state-machine'
import { isLoggedInAsync } from '@mc/user-info'
import { Page as IntroPricingUpgradePage } from '../../experiments/prcng-338_v1_intro_pricing/events/types'
import { isFreeUpgradeTargetEligible } from '../../experiments/prcng-338_v1_intro_pricing/findUpgradeTiers'
import {
  OnCallAddOnMode,
  LearningToolkitAddOnMode as LearningToolkitAddOnModeV3,
} from '../../experiments/prcng-451_v1_on_call_add_on'
import { Page as AddOnPage } from '../../experiments/prcng-451_v1_on_call_add_on/events/types'
import type {
  CertsAddOnMode,
  GiftAddOnMode,
} from '../../experiments/prcng-813_v2_certs_post_purchase'
import { Page as CertsPage } from '../../experiments/prcng-813_v2_certs_post_purchase/events/types'
import { hasCertCreditAddOnPurchase } from '../../experiments/prcng-1138_v1_certs_quiz_plans/hasCertCreditAddOnPurchase'
import { hasCertificateTierPurchase } from '../../experiments/prcng-1194_v1_mc_pro_tier/hasCertificateTierPurchase'
import {
  EMAIL_CAPTURE_SUBSTATES,
  PAYMENT_DETAILS_SUBSTATES,
  STATES,
  STEPS,
  TRANSITIONS,
} from './constants'

export interface MachineContext {
  currentStep: 1 | 2 | 3
  hideNavbarElements: boolean
  theme: 'light' | 'dark' | 'gradient' | 'dark-no-column'
  purchaseSource: Extract<PurchaseSource, 'pre-paywall' | 'logged-in-gift-page'>
  order?: Order
  emailCaptured?: string
  selectedSources?: string[]
  accountName?: string
  isGift: boolean
  isLoggedIn: boolean | null
  trial: FreeTrial
  checkoutPageSource?: string
  selectedPaymentMethod?:
    | typeof STATES.CREDIT_CARD
    | typeof STATES.PAYPAL
    | typeof STATES.APPLE_PAY
  isIntroPricingUpgradeFlowEnabled: boolean // prcng-338_v1
  tiers?: Tier[] // prcng-338_v1
  onCallAddOnMode: OnCallAddOnMode // prcng-451_v1
  giftAddOnModeV3: GiftAddOnMode // prcng-451_v1, prcng-813_v2
  learningToolkitAddOnModeV3: LearningToolkitAddOnModeV3 // prcng-451_v1
  emailCorrectionFlowEnabled: boolean // ce-3314_v1
  emailWithCorrection?: string // ce-3314_v1 - new email entered during email correction flow
  showLtkCongratsModal: boolean // ce-ltk-free-entitlement-modal
  isBraintreeAddOnsEnabled: boolean // com-2275_v1 - Braintree Add-Ons
  isBraintreeGiftsEnabled: boolean // com-2359_v1 - Braintree Gifts
  existingPaymentMethod?: 'paypal' | 'stripe' // com-2530
  certsAddOnMode: CertsAddOnMode // prcng-813_v2
  isCertsRedesignFullBleed: boolean // prcng-1086_v1
  giftingApplePayEnabled: boolean // pen-6752_v1
  hasApplePay: boolean // pen-6752_v1
  isAllAccessBundleProduct?: boolean // prcng-897_v1
  isCertsAddOnCheckout?: boolean // prcng-1138_v1
  isCollapsedPaymentEnabled?: boolean // pen-7377_v1
}

const MACHINE_ID = 'checkout'

// The All-Access bundle already includes the certificate the post-purchase step upsells, so
// it skips it.
// prcng-1138_v1 reads the completed order instead of a flag: its certificate is an
// optional toggle, so intent resolved at mount cannot stand in for what was bought.
const skipsPostPurchaseCerts = (ctx: MachineContext) =>
  !!ctx.isAllAccessBundleProduct || // prcng-961
  hasCertCreditAddOnPurchase(ctx.order) || // prcng-1138_v1
  hasCertificateTierPurchase(ctx.order) // prcng-1194_v1

const hideNavbarElements = assign<MachineContext>({ hideNavbarElements: true })
const showNavbarElements = assign<MachineContext>({ hideNavbarElements: false })

const setCurrentStep = (newState: MachineContext['currentStep']) =>
  assign<MachineContext>({ currentStep: newState })

const setPageSource = (source: MachineContext['checkoutPageSource']) =>
  assign<MachineContext>({ checkoutPageSource: source })

const setSelectedPaymentMethod = (
  selectedPaymentMethod: MachineContext['selectedPaymentMethod'],
) => assign<MachineContext>({ selectedPaymentMethod })

const setTheme = (theme: MachineContext['theme']) =>
  assign<MachineContext>({ theme })

const assignPurchaseSource = assign<MachineContext>(
  (ctx, { data }: DoneEventObject) => {
    const isLoggedIn = data?.isLoggedIn || ctx.isLoggedIn
    const purchaseSource =
      ctx.isGift && !!isLoggedIn ? 'logged-in-gift-page' : 'pre-paywall'

    return { purchaseSource }
  },
)

export const checkoutMachine = createMachine<MachineContext>({
  id: MACHINE_ID,
  predictableActionArguments: true,
  initial: STATES.INITIALIZER,
  states: {
    [STATES.INITIALIZER]: {
      entry: assignPurchaseSource,
      always: [
        {
          cond: ({ isGift }) => isGift,
          target: STATES.MULTI_GIFT_PICKER,
        },
        {
          target: STATES.EMAIL_CAPTURE,
        },
      ],
    },
    [STATES.MULTI_GIFT_PICKER]: {
      entry: [
        setCurrentStep(STEPS.MEMBERSHIP),
        setPageSource(CheckoutEvents.PAGES.MULTI_GIFT_STEP),
        setTheme('gradient'),
      ],
      on: {
        [TRANSITIONS.CONTINUE]: {
          target: STATES.EMAIL_CAPTURE,
          actions: setTheme('light'),
        },
      },
    },
    [STATES.EMAIL_CAPTURE]: {
      initial: STATES.CHECK_IS_LOGGED_IN,
      states: {
        [STATES.CHECK_IS_LOGGED_IN]: {
          always: [
            {
              cond: ({ isLoggedIn }) => isLoggedIn === null,
              target: STATES.FETCH_LOGGED_IN_USER,
            },
            {
              cond: ({ isLoggedIn }) => !isLoggedIn,
              target: STATES.ASK_EMAIL_CAPTURE,
            },
            {
              // multi-gift flow: if user is logged in, skip payment method selection
              // pen-6752_v1: unless variant_1 + apple pay available (show payment method selection)
              // com-2359_v1: unless Braintree gifts enabled (show payment method selection)
              cond: (ctx) =>
                !!(ctx.isLoggedIn && ctx.isGift) &&
                !(ctx.giftingApplePayEnabled && ctx.hasApplePay) &&
                !ctx.isBraintreeGiftsEnabled,
              target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
            },
            {
              // pen-6752_v1: variant_1 gift users with apple pay see payment method selection
              // com-2530: subscribers are auto-routed below; don't intercept them here
              cond: (ctx) =>
                !!(ctx.isLoggedIn && ctx.isGift) &&
                ctx.giftingApplePayEnabled &&
                ctx.hasApplePay &&
                !ctx.existingPaymentMethod,
              target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
            },
            {
              // com-2530: logged-in Braintree subscriber skips payment method selection
              cond: (ctx) =>
                !!(ctx.isLoggedIn && ctx.isGift) &&
                ctx.isBraintreeGiftsEnabled &&
                ctx.existingPaymentMethod === 'paypal',
              target: `#${MACHINE_ID}.${PAYMENT_DETAILS_SUBSTATES.PAYPAL}`,
            },
            {
              // com-2530: logged-in Stripe subscriber skips payment method selection
              cond: (ctx) =>
                !!(ctx.isLoggedIn && ctx.isGift) &&
                ctx.isBraintreeGiftsEnabled &&
                ctx.existingPaymentMethod === 'stripe',
              target: `#${MACHINE_ID}.${PAYMENT_DETAILS_SUBSTATES.CREDIT_CARD}`,
            },
            {
              // com-2359_v1: variant_1 gift users route through payment method selection
              cond: (ctx) =>
                !!(ctx.isLoggedIn && ctx.isGift) && ctx.isBraintreeGiftsEnabled,
              target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
            },
            {
              // pen-7377_v1
              cond: (ctx) =>
                !!ctx.isLoggedIn &&
                !ctx.isGift &&
                !!ctx.isCollapsedPaymentEnabled,
              target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
            },
            {
              cond: ({ isGift, isLoggedIn }) => !!isLoggedIn && !isGift,
              target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
            },
            {
              target: STATES.ASK_EMAIL_CAPTURE,
            },
          ],
        },
        [STATES.FETCH_LOGGED_IN_USER]: {
          invoke: {
            id: 'isLoggedInUser',
            src: () => isLoggedInAsync(true),
            onDone: [
              {
                cond: (_, { data: isLoggedIn }) => !isLoggedIn,
                target: STATES.ASK_EMAIL_CAPTURE,
              },
              {
                // pen-6752_v1: variant_1 gift users with apple pay see payment method selection
                // com-2530: subscribers are auto-routed below; don't intercept them here
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn &&
                  ctx.isGift &&
                  ctx.giftingApplePayEnabled &&
                  ctx.hasApplePay &&
                  !ctx.existingPaymentMethod,
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
              },
              {
                // com-2530: logged-in Braintree subscriber skips payment method selection
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn &&
                  ctx.isGift &&
                  ctx.isBraintreeGiftsEnabled &&
                  ctx.existingPaymentMethod === 'paypal',
                target: `#${MACHINE_ID}.${PAYMENT_DETAILS_SUBSTATES.PAYPAL}`,
              },
              {
                // com-2530: logged-in Stripe subscriber skips payment method selection
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn &&
                  ctx.isGift &&
                  ctx.isBraintreeGiftsEnabled &&
                  ctx.existingPaymentMethod === 'stripe',
                target: `#${MACHINE_ID}.${PAYMENT_DETAILS_SUBSTATES.CREDIT_CARD}`,
              },
              {
                // com-2359_v1: variant_1 gift users route through payment method selection
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn && ctx.isGift && ctx.isBraintreeGiftsEnabled,
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
              },
              {
                // multi-gift flow: logged-in gift users skip payment method selection
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn && ctx.isGift && !ctx.isBraintreeGiftsEnabled,
                target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
              },
              {
                // pen-7377_v1
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn &&
                  !ctx.isGift &&
                  !!ctx.isCollapsedPaymentEnabled,
                target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
              },
              {
                cond: (ctx, { data: isLoggedIn }) =>
                  !!isLoggedIn && !ctx.isGift,
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
              },
            ],
            onError: {
              target: STATES.ASK_EMAIL_CAPTURE,
            },
          },
        },
        [STATES.ASK_EMAIL_CAPTURE]: {
          entry: [
            setCurrentStep(STEPS.ACCOUNT),
            setPageSource(CheckoutEvents.PAGES.ACCOUNT_CREATION),
          ],
          on: {
            // pen-7276_v1 — only gift buyers have a step behind this one. For
            // everyone else the plans page is genuinely the previous page, which
            // is where browser back already goes.
            [TRANSITIONS.GO_TO_PREVIOUS_STEP]: [
              {
                cond: (ctx) => ctx.isGift,
                target: `#${MACHINE_ID}.${STATES.MULTI_GIFT_PICKER}`,
              },
            ],
            [TRANSITIONS.EMAIL_CAPTURED]: [
              {
                // multi-gift flow: only credit card is supported so we skip payment method selection
                // pen-6752_v1: unless variant_1 + apple pay available (show payment method selection)
                // com-2359_v1: unless Braintree gifts enabled (show payment method selection)
                cond: (ctx) =>
                  ctx.isGift &&
                  !(ctx.giftingApplePayEnabled && ctx.hasApplePay) &&
                  !ctx.isBraintreeGiftsEnabled,
                target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
                actions: assign((_, event) => ({
                  emailCaptured: event?.emailCaptured,
                })),
              },
              {
                // pen-6752_v1: variant_1 gift users with apple pay see payment method selection
                cond: (ctx) =>
                  ctx.isGift && ctx.giftingApplePayEnabled && ctx.hasApplePay,
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
                actions: assign((_, event) => ({
                  emailCaptured: event?.emailCaptured,
                })),
              },
              {
                // com-2359_v1: variant_1 gift users route through payment method selection
                cond: (ctx) => ctx.isGift && ctx.isBraintreeGiftsEnabled,
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
                actions: assign((_, event) => ({
                  emailCaptured: event?.emailCaptured,
                })),
              },
              {
                cond: (ctx) => ctx.trial?.eligible,
                target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
              },
              {
                // pen-7377_v1
                cond: (ctx) => !!ctx.isCollapsedPaymentEnabled,
                target: `#${MACHINE_ID}.${STATES.PAYMENT}`,
                actions: assign((_, event) => ({
                  emailCaptured: event?.emailCaptured,
                })),
              },
              {
                target: `#${MACHINE_ID}.${STATES.CHOOSE_PAYMENT_METHOD}`,
                actions: assign((_, event) => ({
                  emailCaptured: event?.emailCaptured,
                })),
              },
            ],
          },
        },
      },
    },
    [STATES.CHOOSE_PAYMENT_METHOD]: {
      entry: [
        setCurrentStep(STEPS.PAYMENT),
        setPageSource(CheckoutEvents.PAGES.PAYMENT_STEP.CHOOSE_PAYMENT),
      ],
      on: {
        [TRANSITIONS.STRIPE_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.CREDIT_CARD,
        },
        [TRANSITIONS.BRAINTREE_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.PAYPAL,
        },
        [TRANSITIONS.APPLE_PAY_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.APPLE_PAY,
        },
        // pen-7276_v1 — no branch for logged-in non-gift buyers: they reach this
        // step straight from the plans page, so there is no in-flow step behind
        // them and browser back should leave checkout as it does today.
        [TRANSITIONS.GO_TO_PREVIOUS_STEP]: [
          {
            cond: (ctx) => !!(ctx.isGift && ctx.isLoggedIn),
            target: STATES.MULTI_GIFT_PICKER,
          },
          {
            cond: (ctx) => !ctx.isLoggedIn,
            target: `#${MACHINE_ID}.${EMAIL_CAPTURE_SUBSTATES.ASK_EMAIL_CAPTURE}`,
          },
        ],
      },
    },
    [STATES.PAYMENT]: {
      initial: STATES.CREDIT_CARD,
      entry: [
        setCurrentStep(STEPS.PAYMENT),
        showNavbarElements,
        assignPurchaseSource,
      ],
      states: {
        [STATES.CREDIT_CARD]: {
          entry: [
            setSelectedPaymentMethod(STATES.CREDIT_CARD),
            setPageSource(CheckoutEvents.PAGES.PAYMENT_STEP.IN_CC_INPUT),
          ],
        },
        [STATES.PAYPAL]: {
          entry: [
            setSelectedPaymentMethod(STATES.PAYPAL),
            setPageSource(CheckoutEvents.PAGES.PAYMENT_STEP.IN_PAYPAL_INPUT),
          ],
        },
        [STATES.APPLE_PAY]: {
          entry: [
            setSelectedPaymentMethod(STATES.APPLE_PAY),
            setPageSource(CheckoutEvents.PAGES.PAYMENT_STEP.IN_APPLE_PAY_INPUT),
          ],
        },
      },
      on: {
        [TRANSITIONS.EMAIL_UPDATED]: {
          actions: assign((_, event) => ({
            emailCaptured: event?.email,
          })),
        },
        [TRANSITIONS.PURCHASE_COMPLETED]: {
          target: STATES.SETUP_ACCOUNT,
          actions: assign((_, event) => ({ order: event.order })),
        },
        [TRANSITIONS.GO_TO_PREVIOUS_STEP]: [
          {
            // pen-6752_v1: variant_1 gift users with apple pay came from CHOOSE_PAYMENT_METHOD
            // (com-2530: exclude auto-routed subscribers who bypassed CHOOSE_PAYMENT_METHOD)
            cond: (ctx) =>
              ctx.isGift &&
              ctx.giftingApplePayEnabled &&
              ctx.hasApplePay &&
              !ctx.existingPaymentMethod,
            target: STATES.CHOOSE_PAYMENT_METHOD,
          },
          {
            // com-2359_v1: gift users who went through payment method selection
            // (com-2530: exclude auto-routed subscribers who bypassed CHOOSE_PAYMENT_METHOD)
            cond: (ctx) =>
              ctx.isGift &&
              ctx.isBraintreeGiftsEnabled &&
              !ctx.existingPaymentMethod,
            target: STATES.CHOOSE_PAYMENT_METHOD,
          },
          {
            // multi-gift flow: don't show payment method selection
            cond: (ctx) => !!(ctx.isGift && !ctx.isLoggedIn),
            target: STATES.EMAIL_CAPTURE,
          },
          {
            // multi-gift flow: if user is logged in then email capture is invalid; go back to multi-gift picker
            cond: (ctx) => !!(ctx.isGift && ctx.isLoggedIn),
            target: STATES.MULTI_GIFT_PICKER,
          },
          {
            // pen-7276_v1 — logged-out trial-eligible buyers jump here straight
            // from email capture, so the default below would send them forward
            // into a payment-method screen they never saw. Trial eligibility is
            // a guest-pass lookup and says nothing about auth, so a signed-in
            // buyer on the same link arrives via CHOOSE_PAYMENT_METHOD and must
            // fall through to it instead.
            cond: (ctx) =>
              !ctx.isGift && !ctx.isLoggedIn && !!ctx.trial?.eligible,
            target: `#${MACHINE_ID}.${EMAIL_CAPTURE_SUBSTATES.ASK_EMAIL_CAPTURE}`,
          },
          {
            // pen-7377_v1 — the collapsed step replaces CHOOSE_PAYMENT_METHOD;
            // the default below would bounce the buyer forward into `payment`.
            cond: (ctx) =>
              !ctx.isGift && !ctx.isLoggedIn && !!ctx.isCollapsedPaymentEnabled,
            target: `#${MACHINE_ID}.${EMAIL_CAPTURE_SUBSTATES.ASK_EMAIL_CAPTURE}`,
          },
          {
            // pen-7377_v1 — a logged-in buyer on the collapsed step has no
            // checkout step behind them, so back must not be possible at all:
            // pen-7276_v1 arms browser back from `state.can(...)`.
            cond: (ctx) =>
              !!ctx.isGift || !ctx.isLoggedIn || !ctx.isCollapsedPaymentEnabled,
            target: STATES.CHOOSE_PAYMENT_METHOD,
          },
        ],
        [TRANSITIONS.STRIPE_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.CREDIT_CARD,
        },
        [TRANSITIONS.BRAINTREE_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.PAYPAL,
        },
        [TRANSITIONS.APPLE_PAY_CHOSEN]: {
          target: PAYMENT_DETAILS_SUBSTATES.APPLE_PAY,
        },
      },
    },
    [STATES.SETUP_ACCOUNT]: {
      entry: [
        hideNavbarElements,
        setPageSource(CheckoutEvents.PAGES.CART_REGISTRATION),
      ],
      on: {
        [TRANSITIONS.EMAIL_CORRECTION_NEEDED]: {
          target: STATES.EMAIL_CORRECTION,
          cond: (ctx) =>
            !!ctx.order?.needsSetup && !!ctx.emailCorrectionFlowEnabled,
        },
        [TRANSITIONS.ACCOUNT_SET]: [
          {
            target: STATES.BOGO,
            cond: (ctx) => !!ctx.order?.bogoGiftToken && !ctx.isGift,
            actions: assign((_, event) => ({
              accountName: event?.accountName,
            })),
          },
          {
            // prcng-338_v1
            target: STATES.INTRO_PRICING_UPGRADE,
            cond: (ctx) =>
              ctx.isIntroPricingUpgradeFlowEnabled &&
              !ctx.isGift &&
              (ctx.order?.products[0].hierarchy ?? TierHierarchy.INDIVIDUAL) <
                TierHierarchy.FAMILY &&
              isFreeUpgradeTargetEligible(ctx.tiers ?? [], ctx.order),
          },
          {
            // prcng-813_v2 — Certs first in add-on sequence
            // prcng-961 — Skip when the buyer already has the cert
            target: STATES.CERTS_POST_PURCHASE,
            cond: (ctx) =>
              ctx.certsAddOnMode === 'post-registration' &&
              !skipsPostPurchaseCerts(ctx) &&
              (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                  ctx.isBraintreeGiftsEnabled)),
          },
          {
            // prcng-451_v1
            target: STATES.GIFT_ADD_ON_V3,
            cond: (ctx) =>
              ctx.giftAddOnModeV3 === 'post-registration' &&
              (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                  ctx.isBraintreeGiftsEnabled)),
          },
          {
            // prcng-961 — When bundle skipped certs, fire gift here even
            // though its mode chained off the skipped state. Without this,
            // gift would be orphaned because its mode never matches
            // 'post-registration'.
            target: STATES.GIFT_ADD_ON_V3,
            cond: (ctx) =>
              skipsPostPurchaseCerts(ctx) &&
              ctx.giftAddOnModeV3 === 'post-certs' &&
              (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                  ctx.isBraintreeGiftsEnabled)),
          },
          {
            // prcng-451_v1 & com-2275_v1
            target: `#${MACHINE_ID}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3}`,
            cond: (ctx) =>
              ctx.isBraintreeAddOnsEnabled &&
              ctx.learningToolkitAddOnModeV3 === 'post-gift',
          },
          {
            target: STATES.GUEST_PASS,
            cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
          },
          {
            target: STATES.REFERRAL_SURVEY,
            cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
          },
          {
            target: STATES.FINISH_CHECKOUT,
          },
        ],
      },
    },
    [STATES.GIFT_ADD_ON_V3]: {
      // prcng-451_v1
      entry: [setTheme('dark'), setPageSource(AddOnPage.AddOnPostPurchasePage)],
      initial: STATES.GIFT_ADD_ON_V3_PURCHASE,
      states: {
        [STATES.GIFT_ADD_ON_V3_PURCHASE]: {
          entry: setPageSource(AddOnPage.AddOnPostPurchasePage),
          on: {
            [TRANSITIONS.GIFT_ADD_ON_PURCHASE_COMPLETED]: {
              target: STATES.GIFT_ADD_ON_V3_THANKS,
            },
            [TRANSITIONS.ADD_ON_PURCHASE_ERROR]: {
              target: STATES.GIFT_ADD_ON_V3_ERROR,
            },
            [TRANSITIONS.GIFT_ADD_ON_CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3}`,
                cond: (ctx) =>
                  ctx.isBraintreeAddOnsEnabled &&
                  ctx.learningToolkitAddOnModeV3 === 'post-gift',
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.GIFT_ADD_ON_V3_THANKS]: {
          entry: setPageSource(AddOnPage.ThanksPostPurchasePage),
          on: {
            [TRANSITIONS.GIFT_ADD_ON_CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3}`,
                cond: (ctx) =>
                  ctx.isBraintreeAddOnsEnabled &&
                  ctx.learningToolkitAddOnModeV3 === 'post-gift',
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.GIFT_ADD_ON_V3_ERROR]: {
          entry: setPageSource(AddOnPage.PaymentErrorPostPurchasePage),
          on: {
            [TRANSITIONS.CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3}`,
                cond: (ctx) =>
                  ctx.isBraintreeAddOnsEnabled &&
                  ctx.learningToolkitAddOnModeV3 === 'post-gift',
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
      },
    },
    [STATES.LEARNING_TOOLKIT_ADD_ON_V3]: {
      // prcng-451_v1
      entry: [setTheme('dark')],
      initial: STATES.LEARNING_TOOLKIT_ADD_ON_V3_MAIN,
      states: {
        [STATES.LEARNING_TOOLKIT_ADD_ON_V3_MAIN]: {
          initial: STATES.LEARNING_TOOLKIT_ADD_ON_V3_LIST,
          entry: setPageSource(AddOnPage.AddOnPostPurchasePage),
          states: {
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_LIST]: {
              on: {
                [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_NEXT]: [
                  {
                    target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_AI,
                    cond: (_, { toolkit }) => toolkit === 'ai',
                  },
                  {
                    target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_BOOKMARKS,
                    cond: (_, { toolkit }) => toolkit === 'bookmarks',
                  },
                  {
                    target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_NOTES,
                    cond: (_, { toolkit }) => toolkit === 'notes',
                  },
                  {
                    target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_RECIPE_CARDS,
                    cond: (_, { toolkit }) => toolkit === 'recipeCards',
                  },
                  {
                    target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_CLASS_GUIDES,
                    cond: (_, { toolkit }) => toolkit === 'classGuides',
                  },
                ],
              },
            },
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_AI]: {},
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_RECIPE_CARDS]: {},
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_BOOKMARKS]: {},
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_NOTES]: {},
            [STATES.LEARNING_TOOLKIT_ADD_ON_V3_CLASS_GUIDES]: {},
          },
          on: {
            [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_BACK]: `${STATES.LEARNING_TOOLKIT_ADD_ON_V3_MAIN}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3_LIST}`,
            [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_CONTINUE]:
              STATES.LEARNING_TOOLKIT_ADD_ON_V3_THANKS,
            [TRANSITIONS.ADD_ON_PURCHASE_ERROR]:
              STATES.LEARNING_TOOLKIT_ADD_ON_V3_ERROR,
            [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_CANCEL]: [
              {
                // ce-ltk-free-entitlement-modal: Show congrats modal when flag is ON
                target: STATES.LEARNING_TOOLKIT_ADD_ON_V3_CONGRATS,
                cond: (ctx) => ctx.showLtkCongratsModal,
              },
              {
                target: `#${MACHINE_ID}.${STATES.ON_CALL_ADD_ON}`,
                // prcng-961 — Skip for All-Access Bundle product
                cond: (ctx) =>
                  ctx.onCallAddOnMode === 'post-toolkit' &&
                  !ctx.isAllAccessBundleProduct,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.LEARNING_TOOLKIT_ADD_ON_V3_THANKS]: {
          entry: setPageSource(AddOnPage.ThanksPostPurchasePage),
          on: {
            [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.ON_CALL_ADD_ON}`,
                // prcng-961 — Skip for All-Access Bundle product
                cond: (ctx) =>
                  ctx.onCallAddOnMode === 'post-toolkit' &&
                  !ctx.isAllAccessBundleProduct,
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.LEARNING_TOOLKIT_ADD_ON_V3_CONGRATS]: {
          entry: [
            setTheme('light'),
            setPageSource(AddOnPage.CongratsPostPurchasePage),
          ],
          on: {
            [TRANSITIONS.LEARNING_TOOLKIT_ADD_ON_CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.ON_CALL_ADD_ON}`,
                // prcng-961 — Skip for All-Access Bundle product
                cond: (ctx) =>
                  ctx.onCallAddOnMode === 'post-toolkit' &&
                  !ctx.isAllAccessBundleProduct,
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.LEARNING_TOOLKIT_ADD_ON_V3_ERROR]: {
          entry: setPageSource(AddOnPage.PaymentErrorPostPurchasePage),
          on: {
            [TRANSITIONS.CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.ON_CALL_ADD_ON}`,
                // prcng-961 — Skip for All-Access Bundle product
                cond: (ctx) =>
                  ctx.onCallAddOnMode === 'post-toolkit' &&
                  !ctx.isAllAccessBundleProduct,
              },
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
      },
    },
    [STATES.ON_CALL_ADD_ON]: {
      // prcng-451_v1
      entry: [setTheme('dark'), setPageSource(AddOnPage.AddOnPostPurchasePage)],
      initial: STATES.ON_CALL_ADD_ON_PURCHASE,
      states: {
        [STATES.ON_CALL_ADD_ON_PURCHASE]: {
          entry: setPageSource(AddOnPage.AddOnPostPurchasePage),
          initial: STATES.ON_CALL_ADD_ON_PURCHASE_LIST,
          states: {
            [STATES.ON_CALL_ADD_ON_PURCHASE_LIST]: {
              on: {
                [TRANSITIONS.EXPAND]: [
                  {
                    target: STATES.ON_CALL_ADD_ON_PURCHASE_GUIDANCE,
                    cond: (_, { kind }) => kind === 'guidance',
                  },
                  {
                    target: STATES.ON_CALL_ADD_ON_PURCHASE_ROLEPLAYS,
                    cond: (_, { kind }) => kind === 'roleplays',
                  },
                  {
                    target: STATES.ON_CALL_ADD_ON_PURCHASE_FEEDBACK,
                    cond: (_, { kind }) => kind === 'feedback',
                  },
                ],
              },
            },
            [STATES.ON_CALL_ADD_ON_PURCHASE_GUIDANCE]: {},
            [STATES.ON_CALL_ADD_ON_PURCHASE_ROLEPLAYS]: {},
            [STATES.ON_CALL_ADD_ON_PURCHASE_FEEDBACK]: {},
          },
          on: {
            [TRANSITIONS.BACK]: `${STATES.ON_CALL_ADD_ON_PURCHASE}.${STATES.ON_CALL_ADD_ON_PURCHASE_LIST}`,
            [TRANSITIONS.CONTINUE]: STATES.ON_CALL_ADD_ON_THANKS,
            [TRANSITIONS.ADD_ON_PURCHASE_ERROR]: STATES.ON_CALL_ADD_ON_ERROR,
            [TRANSITIONS.CANCEL]: [
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.ON_CALL_ADD_ON_THANKS]: {
          entry: setPageSource(AddOnPage.ThanksPostPurchasePage),
          on: {
            [TRANSITIONS.CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
        [STATES.ON_CALL_ADD_ON_ERROR]: {
          entry: setPageSource(AddOnPage.PaymentErrorPostPurchasePage),
          on: {
            [TRANSITIONS.CONTINUE]: [
              {
                target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
                cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
                cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
                actions: setTheme('light'),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
                actions: setTheme('light'),
              },
            ],
          },
        },
      },
    },
    [STATES.CERTS_POST_PURCHASE]: {
      // prcng-813_v2
      entry: [setTheme('dark'), setPageSource(CertsPage.AddOnPostPurchasePage)],
      initial: STATES.CERTS_POST_PURCHASE_MAIN,
      states: {
        [STATES.CERTS_POST_PURCHASE_MAIN]: {
          initial: STATES.CERTS_POST_PURCHASE_MAIN_DEFAULT,
          states: {
            // No entry — the parent already set 'dark', which is the default look.
            [STATES.CERTS_POST_PURCHASE_MAIN_DEFAULT]: {
              always: [
                {
                  cond: (ctx) => ctx.isCertsRedesignFullBleed,
                  target: STATES.CERTS_POST_PURCHASE_MAIN_REDESIGN,
                },
              ],
            },
            // prcng-1086_v1 — full-bleed so the redesigned carousel clears the 596px column.
            [STATES.CERTS_POST_PURCHASE_MAIN_REDESIGN]: {
              entry: setTheme('dark-no-column'),
            },
          },
          on: {
            [TRANSITIONS.CONTINUE]: STATES.CERTS_POST_PURCHASE_THANKS,
            [TRANSITIONS.ADD_ON_PURCHASE_ERROR]:
              STATES.CERTS_POST_PURCHASE_ERROR,
          },
        },
        [STATES.CERTS_POST_PURCHASE_THANKS]: {
          entry: [
            setTheme('dark'),
            setPageSource(CertsPage.ThanksPostPurchasePage),
          ],
        },
        [STATES.CERTS_POST_PURCHASE_ERROR]: {
          entry: [
            setTheme('dark'),
            setPageSource(CertsPage.PaymentErrorPostPurchasePage),
          ],
        },
      },
      on: {
        [TRANSITIONS.CONTINUE]: [
          {
            // prcng-836: After certs → Gift
            target: `#${MACHINE_ID}.${STATES.GIFT_ADD_ON_V3}`,
            cond: (ctx) =>
              ctx.giftAddOnModeV3 === 'post-certs' &&
              (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                  ctx.isBraintreeGiftsEnabled)),
          },
          {
            target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
            cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
            actions: setTheme('light'),
          },
          {
            target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
            cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
            actions: setTheme('light'),
          },
          {
            target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
            actions: setTheme('light'),
          },
        ],
        // prcng-828: "No Thanks" from CertsMain skips the upsell
        [TRANSITIONS.CANCEL]: [
          {
            // prcng-836: After certs → Gift
            target: `#${MACHINE_ID}.${STATES.GIFT_ADD_ON_V3}`,
            cond: (ctx) =>
              ctx.giftAddOnModeV3 === 'post-certs' &&
              (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                  ctx.isBraintreeGiftsEnabled)),
          },
          {
            target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
            cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
            actions: setTheme('light'),
          },
          {
            target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
            cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
            actions: setTheme('light'),
          },
          {
            target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
            actions: setTheme('light'),
          },
        ],
      },
    },
    [STATES.INTRO_PRICING_UPGRADE]: {
      // prcng-338_v1
      initial: STATES.INTRO_PRICING_UPGRADE_OFFER,
      states: {
        [STATES.INTRO_PRICING_UPGRADE_OFFER]: {
          entry: [setPageSource(IntroPricingUpgradePage.FreeUpgradeScreen)],
          on: {
            [TRANSITIONS.CONTINUE]: STATES.INTRO_PRICING_UPGRADE_COMPLETED,
            [TRANSITIONS.CANCEL]: STATES.INTRO_PRICING_UPGRADE_EXIT,
          },
        },
        [STATES.INTRO_PRICING_UPGRADE_COMPLETED]: {
          entry: [setPageSource(IntroPricingUpgradePage.FreeUpgradeCompleted)],
          on: { [TRANSITIONS.CONTINUE]: STATES.INTRO_PRICING_UPGRADE_EXIT },
        },
        [STATES.INTRO_PRICING_UPGRADE_EXIT]: {
          always: [
            {
              // prcng-813_v2 — Certs first in add-on sequence
              // prcng-961 — Skip when the buyer already has the cert
              target: `#${MACHINE_ID}.${STATES.CERTS_POST_PURCHASE}`,
              cond: (ctx) =>
                ctx.certsAddOnMode === 'post-registration' &&
                !skipsPostPurchaseCerts(ctx) &&
                (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                  ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                  (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                    ctx.isBraintreeGiftsEnabled)),
            },
            {
              // prcng-451_v1
              target: `#${MACHINE_ID}.${STATES.GIFT_ADD_ON_V3}`,
              cond: (ctx) =>
                ctx.giftAddOnModeV3 === 'post-registration' &&
                (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                  ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                  (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                    ctx.isBraintreeGiftsEnabled)),
            },
            {
              // prcng-961 — When bundle skipped certs, fire gift here even
              // though its mode chained off the skipped state.
              target: `#${MACHINE_ID}.${STATES.GIFT_ADD_ON_V3}`,
              cond: (ctx) =>
                skipsPostPurchaseCerts(ctx) &&
                ctx.giftAddOnModeV3 === 'post-certs' &&
                (ctx?.selectedPaymentMethod === STATES.CREDIT_CARD ||
                  ctx?.selectedPaymentMethod === STATES.APPLE_PAY ||
                  (ctx?.selectedPaymentMethod === STATES.PAYPAL &&
                    ctx.isBraintreeGiftsEnabled)),
            },
            {
              // prcng-451_v1 & com-2275_v1
              target: `#${MACHINE_ID}.${STATES.LEARNING_TOOLKIT_ADD_ON_V3}`,
              cond: (ctx) =>
                ctx.isBraintreeAddOnsEnabled &&
                ctx.learningToolkitAddOnModeV3 === 'post-gift',
            },
            {
              target: `#${MACHINE_ID}.${STATES.GUEST_PASS}`,
              cond: (ctx) => !!ctx.order?.guestPass?.show && !ctx.isGift,
            },
            {
              target: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
              cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
            },
            {
              target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
            },
          ],
        },
      },
    },
    [STATES.REFERRAL_SURVEY]: {
      initial: STATES.REFERRAL_SURVEY_MAIN,
      states: {
        [STATES.REFERRAL_SURVEY_MAIN]: {
          entry: [setPageSource(CheckoutEvents.PAGES.REFERRAL)],
          on: {
            [TRANSITIONS.REFERRAL_SURVEY_SENT]: [
              {
                target: STATES.REFERRAL_SURVEY_AUDIO,
                cond: (_, { selectedSources }) =>
                  hasSelectedAudioReferral(selectedSources),
                actions: assign((_, { selectedSources }) => ({
                  selectedSources,
                })),
              },
              {
                target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
              },
            ],
          },
        },
        [STATES.REFERRAL_SURVEY_AUDIO]: {
          entry: [setPageSource(CheckoutEvents.PAGES.AUDIO_REFERRAL)],
          on: {
            [TRANSITIONS.REFERRAL_SURVEY_AUDIO_SENT]: {
              target: `#${MACHINE_ID}.${STATES.FINISH_CHECKOUT}`,
            },
          },
        },
      },
    },
    [STATES.GUEST_PASS]: {
      initial: STATES.SEND_GUEST_PASS,
      states: {
        [STATES.SEND_GUEST_PASS]: {
          entry: setPageSource(CheckoutEvents.PAGES.GUEST_PASS.INPUT),
          on: {
            [TRANSITIONS.GUEST_PASS_SENT]: {
              target: STATES.GUEST_PASS_SUCCESS,
            },
            [TRANSITIONS.GUEST_PASS_SKIPPED]: {
              target: STATES.SKIP_GUEST_PASS,
            },
          },
        },
        [STATES.SKIP_GUEST_PASS]: {
          entry: setPageSource(CheckoutEvents.PAGES.GUEST_PASS.SKIP),
          on: {
            [TRANSITIONS.GUEST_PASS_STEP_COMPLETED]: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
          },
        },
        [STATES.GUEST_PASS_SUCCESS]: {
          entry: setPageSource(CheckoutEvents.PAGES.GUEST_PASS.SUCCESS),
          on: {
            [TRANSITIONS.GUEST_PASS_STEP_COMPLETED]: `#${MACHINE_ID}.${STATES.REFERRAL_SURVEY}`,
          },
        },
      },
    },
    [STATES.BOGO]: getBogoSendGiftMachineConfig<MachineContext>({
      exitState: `#${MACHINE_ID}.${STATES.BOGO_POST}`,
    }),
    [STATES.BOGO_POST]: {
      always: [
        {
          target: STATES.REFERRAL_SURVEY,
          cond: (ctx) => !!ctx.order?.referralSources && !ctx.isGift,
          actions: setTheme('light'),
        },
        {
          target: STATES.FINISH_CHECKOUT,
          actions: setTheme('light'),
        },
      ],
    },
    [STATES.FINISH_CHECKOUT]: {
      type: 'final',
      entry: (ctx) => {
        if (window?.location) {
          window.location.href = ctx.order?.redirectPath ?? paths.root
        }
      },
    },
    [STATES.ABORT_CHECKOUT]: {
      type: 'final',
      entry: () => {
        if (window?.location) {
          window.location.href = paths.checkout
        }
      },
    },
    [STATES.EMAIL_CORRECTION]: {
      entry: [
        hideNavbarElements,
        setPageSource(CheckoutEvents.PAGES.EMAIL_CORRECTION),
      ],
      on: {
        [TRANSITIONS.EMAIL_CORRECTION_REQUESTED]: {
          target: STATES.EMAIL_CORRECTION_VERIFICATION_SENT,
          actions: assign((_, event) => ({
            emailWithCorrection: event?.newEmail,
          })),
        },
        [TRANSITIONS.EMAIL_CORRECTION_CANCELED]: {
          target: STATES.SETUP_ACCOUNT,
        },
      },
    },
    [STATES.EMAIL_CORRECTION_VERIFICATION_SENT]: {
      entry: [
        hideNavbarElements,
        setPageSource(CheckoutEvents.PAGES.EMAIL_CORRECTION_VERIFICATION_SENT),
      ],
      on: {
        [TRANSITIONS.EMAIL_CORRECTION_NEEDED_AGAIN]: {
          target: STATES.EMAIL_CORRECTION,
        },
      },
    },
  },
})

export const useCheckoutMachine = () => {
  const ctx = useStateMachine<MachineContext>()

  if (!ctx) {
    throw new Error('useCheckoutMachine was used outside of its Provider')
  }

  return ctx
}
